/**
 * Agent Bus ops panel — platform-admin per-runtime monitoring view of
 * bus-service (agent-bus plan §7 P5 Step 13). A platform admin picks a
 * runtime (Fleet → Connect) and sees that runtime's WHOLE bus: the alias
 * directory, per-alias queue depths + unattended flags, the 7-day resolved
 * history, and a force-requeue control for the "stuck alias" runbook
 * scenario (`ai-docs/bus-service-guide.md` §12).
 *
 * Same trust model as RuntimeTasklist / RuntimeFsBrowser / RuntimeGitBrowser:
 * platform-admin already has full authorized fleet access, so this is
 * monitoring visibility, not a privacy expansion. The one MUTATING action
 * (force-requeue) is the operator recovery lever the runbook itself names —
 * not a general bus-write surface (no send/register/cancel here).
 *
 * Data path: REST through the platform proxy (`lib/bus-api.ts`), same shape
 * as the git/tasklist proxies — NOT `useGatewayRpc` (that talks only to the
 * CONNECTED runtime, never a platform-admin-selected target user).
 *
 * Live-polled at the bus's own ~5s cadence (`BUS_POLL_INTERVAL_MS`) so a
 * stale claim's auto-requeue at the 2h claim TTL — or a forced one via the
 * control below — is actually VISIBLE transitioning here, not just true on
 * the broker (the ask this tab exists to satisfy).
 */

import {
  AlertTriangle,
  Clock,
  Inbox,
  Loader2,
  ScrollText,
  RefreshCw,
  RotateCcw,
  Users,
  Waypoints,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatRelative } from "@/components/uploads/upload-time";
import { useRuntimeContext } from "@/hooks/useRuntimeContext";
import { usePolling } from "@/hooks/usePolling";
import {
  BusApiError,
  describeBusError,
  getBusAgents,
  getBusHistory,
  getBusJournal,
  getBusStatus,
  journalDayUTC,
  JOURNAL_EVENTS,
  requeueBusMessage,
  type BusAgentEntry,
  type BusAliasStatus,
  type BusHistoryEntry,
  type BusJournalEvent,
  type BusJournalRow,
} from "@/lib/bus-api";
import {
  BUS_PROBES,
  PROBE_AGENTS,
  PROBE_ALIASES,
  PROBE_HISTORY,
  PROBE_JOURNAL,
  selectAliasDescription,
  selectBusSectionState,
} from "@/lib/bus-section-state";
import { incompleteNotice, probe, unreachableNames } from "@/lib/probe";

const POLL_MS = 5_000; // matches BUS_POLL_INTERVAL_MS (guide §6)
const HISTORY_LIMIT = 50;

/* Probe labels now live in `lib/bus-section-state` (a leaf module) so the test
   that pins them need not import this whole component tree. Re-exported here
   because this page is where they are consumed. */
export { BUS_PROBES } from "@/lib/bus-section-state";

/** Events that mean something went WRONG and left no message behind. The
 *  journal is the only surface that reports them at all — a REFUSE row is
 *  the sole trace that a message was lost. */
const SIGNAL_EVENTS: readonly BusJournalEvent[] = ["REFUSE", "EXPIRE", "REAP"];

const JOURNAL_LIMIT = 200;

/** Journal retention, matching bus-service's own window (guide §4). Bounds the
 *  day picker so an operator cannot silently ask for a reaped partition. */
const JOURNAL_RETENTION_DAYS = 7;

type BusSnapshot = {
  aliases: BusAliasStatus[];
  agents: BusAgentEntry[];
  history: BusHistoryEntry[];
  /** Null when the journal probe FAILED (or no runtime is selected) — which is
   *  a different thing from a day with no events, and must render differently. */
  journal: { day: string; truncated: boolean; rows: BusJournalRow[] } | null;
  /** Labels of the probes that FAILED this fetch. Empty = everything answered. */
  unreachable: string[];
};

/** Row tint for the journal. `REFUSE` is the highest-signal row on the whole
 *  panel — it means a message was LOST — so it is the one that gets colour. */
function journalEventClass(event: BusJournalEvent): string {
  if (event === "REFUSE") return "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400";
  if (event === "EXPIRE" || event === "REAP")
    return "bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400";
  if (event === "HANDBACK") return "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400";
  return "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400";
}

function statusBadgeClass(status: BusHistoryEntry["status"]): string {
  switch (status) {
    case "done":
      return "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400";
    case "failed":
      return "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400";
    case "expired":
    case "canceled":
      return "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400";
    default:
      return "bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400";
  }
}

export function RuntimeBusOps() {
  const { target } = useRuntimeContext();
  const userId = target?.userId ?? null;

  /* Journal filters. `day` empty means "let the SERVICE pick its own today" —
     the safe default, because the service partitions on its clock and this
     host runs UTC-3 (handoff trap 1). When the operator does step back through
     the 7-day window we send an explicit day, computed in UTC. */
  const [jEvent, setJEvent] = useState<"" | BusJournalEvent>("");
  const [jAlias, setJAlias] = useState("");
  const [jThread, setJThread] = useState("");
  const [jDay, setJDay] = useState("");

  const [requeueId, setRequeueId] = useState("");
  const [requeuing, setRequeuing] = useState(false);
  const [requeueMsg, setRequeueMsg] = useState<string | null>(null);
  const [requeueIsError, setRequeueIsError] = useState(false);

  const fetcher = useMemo(
    () => async (): Promise<BusSnapshot> => {
      if (!userId) return { aliases: [], agents: [], history: [], journal: null, unreachable: [] };
      /* SETTLED probes, not a bare `Promise.all` (see `lib/probe.ts`). Two
         reasons, and the second is the one that bit:
         1. A rejected fetch used to leave `data` null, so every `?? []`
            fallback below rendered a failure as an empty bus.
         2. The four calls are INDEPENDENT. Collapsing them meant a 500 from
            /bus/history also blanked the ALIAS table — during exactly the
            incident this tab exists to diagnose. Now it also matters for the
            journal, which 404s on every container until the Wave-3 roll: that
            one expected failure must not darken the three that work. */
      const [status, agents, history, journal] = await Promise.all([
        probe(getBusStatus(userId)),
        probe(getBusAgents(userId)),
        probe(getBusHistory(userId)),
        probe(
          getBusJournal(userId, {
            day: jDay || undefined,
            event: jEvent || undefined,
            alias: jAlias || undefined,
            thread: jThread || undefined,
            limit: JOURNAL_LIMIT,
          }),
        ),
      ]);
      return {
        aliases: status.ok ? status.value.aliases : [],
        agents: agents.ok ? agents.value.agents : [],
        history: history.ok ? history.value.messages : [],
        /* null, NOT an empty page: the journal 404s on every container until
           the Wave-3 roll, and "the route is not there yet" must never render
           as "nothing happened today". */
        journal: journal.ok ? journal.value : null,
        unreachable: unreachableNames({
          [PROBE_ALIASES]: status,
          [PROBE_AGENTS]: agents,
          [PROBE_HISTORY]: history,
          [PROBE_JOURNAL]: journal,
        }),
      };
    },
    [userId, jDay, jEvent, jAlias, jThread],
  );

  const { data, loading, error, refresh } = usePolling<BusSnapshot>(fetcher, POLL_MS, {
    enabled: !!userId,
  });

  /* usePolling reads the fetcher through a ref and its effect deps do not
     include it, so a changed filter would otherwise not show until the next
     5 s tick. `refresh` is a `useCallback([])` — stable, so this cannot loop.

     ⚠ IDEMPOTENT BY STATE, NOT BY RUN COUNT. This was first written as a
     `mounted` ref that skipped its FIRST invocation, on the reasoning that
     usePolling already fetches once on mount. That is not idempotence, it is
     counting — and `main.tsx` wraps the app in <StrictMode>, which deliberately
     double-invokes effects in dev: the second run saw `mounted === true` and
     fired exactly the duplicate fetch the guard existed to prevent. Comparing
     the SIGNATURE instead is correct however many times React runs this, which
     is the property a run-counter can never have. (Same lesson the plugin's
     concierge poller learned the hard way the same day: `register(api)` runs
     more than once per process, and a subsystem that assumes "once" gets two of
     everything.) */
  const fetchedFor = useRef<string | null>(null);
  useEffect(() => {
    const signature = JSON.stringify([userId, jDay, jEvent, jAlias, jThread]);
    // First run records what usePolling's own mount fetch is already covering.
    if (fetchedFor.current === null) {
      fetchedFor.current = signature;
      return;
    }
    if (fetchedFor.current === signature) return; // nothing actually changed
    fetchedFor.current = signature;
    if (userId) refresh();
  }, [userId, jDay, jEvent, jAlias, jThread, refresh]);

  const descriptionByAlias = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of data?.agents ?? []) m.set(a.alias, a.description);
    return m;
  }, [data?.agents]);

  const handleForceRequeue = async () => {
    const id = requeueId.trim();
    if (!userId || !id) return;
    setRequeuing(true);
    setRequeueMsg(null);
    try {
      await requeueBusMessage(userId, id);
      setRequeueMsg(`Requeued ${id} — it should show as queued again above within ${POLL_MS / 1000}s.`);
      setRequeueIsError(false);
      setRequeueId("");
      refresh();
    } catch (e) {
      setRequeueMsg(e instanceof BusApiError ? describeBusError(e) : "Force-requeue failed.");
      setRequeueIsError(true);
    } finally {
      setRequeuing(false);
    }
  };

  if (!userId) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <Waypoints className="h-8 w-8 text-slate-400" />
        <h1 className="mt-2 text-xl font-semibold">Agent Bus</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Select a runtime from the fleet to view its agent-bus state.
        </p>
      </div>
    );
  }

  const aliases = data?.aliases ?? [];
  const history = (data?.history ?? []).slice(0, HISTORY_LIMIT);

  /* `data === null` means NO fetch has settled — never "the bus is empty".
     The selector encodes that precedence and is unit-tested for it. */
  const settledUnreachable = data?.unreachable ?? null;
  const aliasState = selectBusSectionState({
    probe: PROBE_ALIASES,
    unreachable: settledUnreachable,
    failed: !!error,
    count: aliases.length,
  });
  const historyState = selectBusSectionState({
    probe: PROBE_HISTORY,
    unreachable: settledUnreachable,
    failed: !!error,
    count: history.length,
  });
  const journalRows = data?.journal?.rows ?? [];
  const journalState = selectBusSectionState({
    probe: PROBE_JOURNAL,
    unreachable: settledUnreachable,
    failed: !!error,
    count: journalRows.length,
  });
  const agentsState = selectBusSectionState({
    probe: PROBE_AGENTS,
    unreachable: settledUnreachable,
    failed: !!error,
    count: data?.agents.length ?? 0,
  });
  const partial = incompleteNotice(data?.unreachable ?? [], BUS_PROBES.length);

  /* ── The D-8 ignition inference (handoff ask C) ────────────────────────
     Concierge registration lives INSIDE the poller
     (`concierge-poller.ts:177`), and the poller only starts under
     `BUS_CONCIERGE_POLL === "1"` (`index.ts:1568`). So a directory that
     ANSWERED and contains no concierge row means ignition is off — or the
     poller failed to start. Two readings, and we cannot tell them apart from
     here, so the copy states both and never renders a green/red verdict.

     Gated on the DIRECTORY probe (`/bus/agents`), not the status probe, and
     suppressed entirely when it did not answer — an inference drawn from a
     failed read is the same bug this panel just stopped making. */
  const directoryAnswered = agentsState.kind === "rows" || agentsState.kind === "empty";
  const conciergeAbsent =
    directoryAnswered && !(data?.agents ?? []).some((a) => a.alias === "concierge" || a.kind === "concierge");

  /* Rows that mean something went wrong and left no message behind. Counted
     over exactly what is SHOWN — the loaded day, narrowed by the active
     filters and capped by `truncated` — and the badge says "shown" for that
     reason. Not "the last 24h": the journal is day-partitioned, so a 24h
     figure would be a claim this data cannot support. */
  const signalCount = journalRows.filter((r) => SIGNAL_EVENTS.includes(r.event)).length;

  return (
    <div className="flex h-full min-h-0 flex-col gap-3" data-testid="runtime-bus-ops">
      {/* Toolbar: refresh + force-requeue (the runbook's stuck-alias recovery lever) */}
      <div
        className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900"
        data-testid="bus-ops-toolbar"
      >
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
        <span className="text-xs text-slate-400">polling every {POLL_MS / 1000}s</span>
        <div className="ml-auto flex items-center gap-2">
          <input
            type="text"
            value={requeueId}
            onChange={(e) => setRequeueId(e.target.value)}
            placeholder="message id (stuck claim) — POST /bus/requeue"
            className="w-72 rounded-md border border-slate-200 px-2 py-1.5 text-xs dark:border-slate-700 dark:bg-slate-800"
            data-testid="bus-ops-requeue-input"
          />
          <button
            type="button"
            onClick={() => void handleForceRequeue()}
            disabled={requeuing || !requeueId.trim()}
            title="Release a stuck claim now instead of waiting out the 2h claim TTL (guide §12)"
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-100 disabled:opacity-50 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-400"
            data-testid="bus-ops-requeue-button"
          >
            {requeuing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
            Force requeue
          </button>
        </div>
      </div>

      {requeueMsg && (
        <div
          className={`shrink-0 rounded-md p-3 text-sm ${
            requeueIsError
              ? "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
              : "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400"
          }`}
          data-testid="bus-ops-requeue-result"
        >
          {requeueMsg}
        </div>
      )}

      {error && (
        <div
          className="shrink-0 rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400"
          data-testid="bus-ops-error"
        >
          {error instanceof BusApiError ? describeBusError(error) : error.message}
        </div>
      )}

      {partial && (
        <div
          className="shrink-0 rounded-md bg-amber-50 p-3 text-sm text-amber-700 dark:bg-amber-900/20 dark:text-amber-400"
          data-testid="bus-ops-partial"
        >
          {partial}
        </div>
      )}

      {conciergeAbsent && (
        <div
          className="shrink-0 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300"
          data-testid="bus-ops-d8-inference"
        >
          <span className="font-medium">No concierge alias registered</span> — ⚖ D-8 delivery is
          inactive, or the poller did not start. While no concierge is registered a send to it is
          refused <code className="font-mono text-xs">unknown_alias</code> rather than queued.
        </div>
      )}

      <div className="grid min-h-0 flex-1 grid-rows-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-4">
        {/* Alias directory + queue depths */}
        <Card className="flex min-h-0 flex-col overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Users className="h-4 w-4" />
              {aliasState.kind === "loading"
                ? "Loading…"
                : aliasState.kind === "unreachable"
                  ? "Registered aliases — could not read"
                  : `${aliases.length} registered alias${aliases.length === 1 ? "" : "es"}`}
            </CardTitle>
          </CardHeader>
          <CardContent className="min-h-0 flex-1 overflow-auto p-0">
            {aliasState.kind === "unreachable" && (
              <p
                className="py-10 text-center text-sm text-amber-600 dark:text-amber-500"
                data-testid="bus-ops-aliases-unreachable"
              >
                Could not read the alias directory. This is NOT “no aliases registered”.
              </p>
            )}
            {aliasState.kind === "empty" && (
              <p className="py-10 text-center text-sm text-slate-400">
                No aliases registered on this runtime's bus yet.
              </p>
            )}
            {aliases.length > 0 && (
              <table className="w-full text-left text-xs" data-testid="bus-ops-alias-table">
                <thead className="sticky top-0 bg-slate-50 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                  <tr>
                    <th className="px-3 py-2 font-medium">Alias</th>
                    <th className="px-3 py-2 font-medium">Kind</th>
                    <th className="px-3 py-2 font-medium">Description</th>
                    <th className="px-3 py-2 text-right font-medium">Queued</th>
                    <th className="px-3 py-2 text-right font-medium">Processing</th>
                    <th className="px-3 py-2 font-medium">Last poll</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {aliases.map((a) => (
                    <tr key={a.alias} data-testid={`bus-ops-alias-row-${a.alias}`}>
                      <td className="px-3 py-2 font-mono">{a.alias}</td>
                      <td className="px-3 py-2">
                        {a.kind}
                        {a.durable && (
                          <span className="ml-1 rounded bg-slate-100 px-1 text-[10px] uppercase text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                            durable
                          </span>
                        )}
                      </td>
                      {/* Three states, never one em-dash for all of them — the
                          alias table is the control the live pass leans on, and
                          a control that can lie is not a control. */}
                      {(() => {
                        const d = selectAliasDescription({
                          directoryAnswered,
                          description: descriptionByAlias.get(a.alias),
                        });
                        if (d.kind === "unread") {
                          return (
                            <td
                              className="px-3 py-2 text-amber-600 dark:text-amber-500"
                              title="The alias directory (/bus/agents) did not answer this poll — this is NOT an empty description."
                              data-testid={`bus-ops-desc-unread-${a.alias}`}
                            >
                              not read
                            </td>
                          );
                        }
                        if (d.kind === "absent") {
                          return (
                            <td
                              className="px-3 py-2 text-amber-600 dark:text-amber-500"
                              title="Present in /bus/status but not /bus/agents. Both read the SAME registry, so this is almost always a reap or unregister landing between the two concurrent polls — expect it to clear on the next one. Only persistence across several polls is a real inconsistency."
                              data-testid={`bus-ops-desc-absent-${a.alias}`}
                            >
                              not in directory
                            </td>
                          );
                        }
                        return (
                          <td
                            className="max-w-xs truncate px-3 py-2 text-slate-500 dark:text-slate-400"
                            title={d.text}
                          >
                            {d.text}
                          </td>
                        );
                      })()}
                      <td className="px-3 py-2 text-right tabular-nums">{a.queued}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{a.processing}</td>
                      <td className="px-3 py-2 text-slate-500 dark:text-slate-400">
                        {a.lastPollAt ? formatRelative(a.lastPollAt) : "never"}
                      </td>
                      <td className="px-3 py-2">
                        {a.unattended ? (
                          <span className="inline-flex items-center gap-1 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-medium uppercase text-red-700 dark:bg-red-900/20 dark:text-red-400">
                            <AlertTriangle className="h-3 w-3" />
                            unattended
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* History — 7-day resolved-message window */}
        <Card className="flex min-h-0 flex-col overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Inbox className="h-4 w-4" />
              {historyState.kind === "loading"
                ? "History — loading…"
                : historyState.kind === "unreachable"
                  ? "History — could not read"
                  : `History (last ${history.length} of the 7-day window)`}
            </CardTitle>
          </CardHeader>
          <CardContent className="min-h-0 flex-1 overflow-auto p-0">
            {historyState.kind === "unreachable" && (
              <p
                className="py-10 text-center text-sm text-amber-600 dark:text-amber-500"
                data-testid="bus-ops-history-unreachable"
              >
                Could not read the message history. This is NOT “no messages”.
              </p>
            )}
            {historyState.kind === "empty" && (
              <p className="py-10 text-center text-sm text-slate-400">
                No resolved messages in the last 7 days.
              </p>
            )}
            {history.length > 0 && (
              <table className="w-full text-left text-xs" data-testid="bus-ops-history-table">
                <thead className="sticky top-0 bg-slate-50 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                  <tr>
                    <th className="px-3 py-2 font-medium">From → To</th>
                    <th className="px-3 py-2 font-medium">Kind</th>
                    <th className="px-3 py-2 font-medium">Thread</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Resolved
                      </span>
                    </th>
                    <th className="px-3 py-2 font-medium">Summary</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {history.map((h) => (
                    <tr key={h.id} data-testid={`bus-ops-history-row-${h.id}`}>
                      <td className="px-3 py-2 font-mono">
                        {h.from} → {h.to}
                      </td>
                      <td className="px-3 py-2">{h.kind}</td>
                      <td className="max-w-[10rem] truncate px-3 py-2 font-mono text-slate-500 dark:text-slate-400" title={h.thread}>
                        {h.thread ?? "—"}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${statusBadgeClass(h.status)}`}>
                          {h.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-500 dark:text-slate-400">
                        {h.resolvedAt ? formatRelative(h.resolvedAt) : "—"}
                      </td>
                      <td className="max-w-xs truncate px-3 py-2 text-slate-500 dark:text-slate-400" title={h.summary}>
                        {h.summary ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Event journal — the ONLY surface that shows an event leaving no
            message behind (REFUSE / REAP / EXPIRE). See the guide §4. */}
        <Card className="flex min-h-0 flex-col overflow-hidden">
          <CardHeader className="pb-2">
            <CardTitle className="flex flex-wrap items-center gap-2 text-sm font-medium text-muted-foreground">
              <ScrollText className="h-4 w-4" />
              {journalState.kind === "loading"
                ? "Event journal — loading…"
                : journalState.kind === "unreachable"
                  ? "Event journal — could not read"
                  : `Event journal — ${journalRows.length} event${journalRows.length === 1 ? "" : "s"}`}
              {/* The day the SERVICE read, never the day we asked for. */}
              {data?.journal && (
                <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                  {data.journal.day}
                </span>
              )}
              {data?.journal?.truncated && (
                <span
                  className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium uppercase text-amber-700 dark:bg-amber-900/20 dark:text-amber-400"
                  title="The cap bit — older events on this day are not shown. Narrow the filters to see them."
                  data-testid="bus-ops-journal-truncated"
                >
                  showing the most recent {JOURNAL_LIMIT} of more
                </span>
              )}
              {journalState.kind === "rows" && signalCount > 0 && (
                <span
                  className="inline-flex items-center gap-1 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-medium uppercase text-red-700 dark:bg-red-900/20 dark:text-red-400"
                  title="REFUSE + EXPIRE + REAP among the events currently shown — narrowed by the filters above, and capped by `truncated`. Each one is a message that was lost or a registration that went stale."
                  data-testid="bus-ops-journal-signal"
                >
                  <AlertTriangle className="h-3 w-3" />
                  {signalCount} lost/stale shown
                </span>
              )}
            </CardTitle>

            {/* Filters. `day` blank = let the service choose its own today. */}
            <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="bus-ops-journal-filters">
              <select
                value={jEvent}
                onChange={(e) => setJEvent(e.target.value as "" | BusJournalEvent)}
                className="rounded-md border border-slate-200 px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-800"
                data-testid="bus-ops-journal-event"
              >
                <option value="">all events</option>
                {JOURNAL_EVENTS.map((ev) => (
                  <option key={ev} value={ev}>
                    {ev}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={jAlias}
                onChange={(e) => setJAlias(e.target.value)}
                placeholder="alias (either end)"
                className="w-44 rounded-md border border-slate-200 px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-800"
                data-testid="bus-ops-journal-alias"
              />
              <input
                type="text"
                value={jThread}
                onChange={(e) => setJThread(e.target.value)}
                placeholder="thread"
                className="w-44 rounded-md border border-slate-200 px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-800"
                data-testid="bus-ops-journal-thread"
              />
              <input
                type="date"
                value={jDay}
                onChange={(e) => setJDay(e.target.value)}
                /* Bounded to the retention window on purpose. A day outside it
                   returns an empty-but-successful 200, which reads as "nothing
                   happened" when the truth is "that partition is gone" — the
                   same empty-looks-like-quiet failure this whole tab was fixed
                   for. Bounds are UTC, matching the service's partitions. */
                min={journalDayUTC(new Date(Date.now() - (JOURNAL_RETENTION_DAYS - 1) * 86_400_000))}
                max={journalDayUTC(new Date())}
                title="Blank = the service's own today. Retention is 7 days, so the picker stops there."
                className="rounded-md border border-slate-200 px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-800"
                data-testid="bus-ops-journal-day"
              />
              {jDay && (
                <button
                  type="button"
                  onClick={() => setJDay("")}
                  className="text-xs text-slate-500 underline hover:text-slate-700 dark:text-slate-400"
                >
                  today
                </button>
              )}
            </div>
          </CardHeader>

          <CardContent className="min-h-0 flex-1 overflow-auto p-0">
            {journalState.kind === "unreachable" && (
              <p
                className="py-10 text-center text-sm text-amber-600 dark:text-amber-500"
                data-testid="bus-ops-journal-unreachable"
              >
                Could not read the event journal. This is NOT “no events”.
              </p>
            )}
            {journalState.kind === "empty" && (
              <p
                className="px-6 py-10 text-center text-sm text-slate-400"
                data-testid="bus-ops-journal-empty"
              >
                {jEvent === "REFUSE"
                  ? "No refused sends recorded on this day."
                  : "No events recorded on this day."}
                {jEvent === "REFUSE" && (
                  <span className="mt-1 block text-xs">
                    Only a <span className="font-mono">send</span> journals a refusal — a poll or
                    claim from an unknown alias is rejected without a row, so this is not “nothing
                    was refused”.
                  </span>
                )}
              </p>
            )}
            {journalState.kind === "rows" && (
              <table className="w-full text-left text-xs" data-testid="bus-ops-journal-table">
                <thead className="sticky top-0 bg-slate-50 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                  <tr>
                    <th className="px-3 py-2 font-medium">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Time
                      </span>
                    </th>
                    <th className="px-3 py-2 font-medium">Event</th>
                    <th className="px-3 py-2 font-medium">From → To</th>
                    <th className="px-3 py-2 font-medium">Kind</th>
                    <th className="px-3 py-2 font-medium">Thread</th>
                    <th className="px-3 py-2 font-medium">Note</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {journalRows.map((r, i) => (
                    <tr key={`${r.ts}-${r.event}-${r.id ?? i}`} data-testid={`bus-ops-journal-row-${i}`}>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-500 dark:text-slate-400">
                        {formatRelative(r.ts)}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${journalEventClass(r.event)}`}
                          /* A CLAIM is NOT a delivery: the broker has no dispatch
                             verb, so a claimed message does not mean a turn ran
                             (328 claim+acks with the poller off produced zero). */
                          title={
                            r.event === "CLAIM"
                              ? "Claimed by the consumer. A claim does not start work — the broker has no dispatch verb."
                              : undefined
                          }
                        >
                          {r.event}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono">
                        {r.from ?? "—"} → {r.to ?? "—"}
                      </td>
                      <td className="px-3 py-2">{r.kind ?? "—"}</td>
                      <td
                        className="max-w-[10rem] truncate px-3 py-2 font-mono text-slate-500 dark:text-slate-400"
                        title={r.thread}
                      >
                        {r.thread ?? "—"}
                      </td>
                      <td
                        className="max-w-xs truncate px-3 py-2 text-slate-500 dark:text-slate-400"
                        title={r.note}
                      >
                        {r.note ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
